import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, getActiveTenant, setActiveTenant } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

/** Super-admin fleet switcher.
 *
 *  Every tenant is a separate fleet with its own vehicles, drivers, trips and settings — the
 *  backend already isolates them on every query. This picks which one the whole panel works in,
 *  so fleets are never looked at mixed together. Only a user with the "*" permission sees it,
 *  and the backend re-checks that on every request; the header alone grants nothing.
 */
export default function TenantSwitcher() {
  const { user, tenant, can } = useAuth();
  const t = useT();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const active = getActiveTenant() || (user as any)?.tenantId || "";

  const tenants = useQuery({
    queryKey: ["tenants"],
    queryFn: async () => (await api.get("/tenants")).data,
    enabled: can("*"),
    staleTime: 300000,
  });

  if (!can("*")) {
    return (
      <div data-testid="tenant-badge" className="flex items-center gap-2 px-3 py-2 rounded-md border border-[#26334d] bg-[#0f141f]">
        <Building2 className="h-3.5 w-3.5 text-slate-500" />
        <span className="text-sm text-slate-100 truncate">{tenant?.name || "—"}</span>
      </div>
    );
  }

  const list: any[] = tenants.data || [];
  const current = list.find((x) => x.id === active);

  const pick = (id: string | null, name: string) => {
    setActiveTenant(id);
    setOpen(false);
    qc.clear();                       // never show one fleet's cached data inside another
    toast.success(`${t("Fleet")}: ${name}`);
    window.location.reload();         // simplest way to guarantee no stale fleet state anywhere
  };

  return (
    <div className="relative">
      <button data-testid="tenant-switcher" onClick={() => setOpen((v) => !v)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md border border-[#26334d] bg-[#0f141f] hover:bg-[#182030]">
        <Building2 className="h-3.5 w-3.5 text-blue-400" />
        <span className="text-sm text-slate-100 truncate flex-1 text-left">
          {current?.name || tenant?.name || "—"}
        </span>
        {list.length > 1 && <span className="font-mono text-[9px] text-slate-500">{list.length}</span>}
        <ChevronDown className={cn("h-3.5 w-3.5 text-slate-500 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div data-testid="tenant-list"
             className="absolute z-50 mt-1 w-full max-h-72 overflow-auto rounded-md border border-[#26334d] bg-[#0d1220] shadow-xl">
          {tenants.isLoading && <div className="px-3 py-2 text-xs text-slate-500">{t("Loading")}…</div>}
          {list.map((x) => (
            <button key={x.id} data-testid={`tenant-option-${x.id}`} onClick={() => pick(x.id, x.name)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#182030]">
              {x.id === active ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <span className="w-3.5" />}
              <span className="text-sm text-slate-200 truncate flex-1">{x.name}</span>
              {x.settings?.demoSeededAt && (
                <span className="font-mono text-[9px] px-1 rounded bg-emerald-500/15 text-emerald-300">DEMO</span>
              )}
            </button>
          ))}
          {!tenants.isLoading && !list.length && (
            <div className="px-3 py-2 text-xs text-slate-500">{t("No data")}</div>
          )}
          {active !== (user as any)?.tenantId && (
            <button data-testid="tenant-reset" onClick={() => pick(null, tenant?.name || "own fleet")}
                    className="w-full px-3 py-2 text-left text-xs text-blue-300 hover:bg-[#182030] border-t border-[#26334d]">
              ← Back to my own fleet
            </button>
          )}
        </div>
      )}
    </div>
  );
}
